import { Computer, Moon, Sun } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useEffect, useState } from "react";
import { useSystemSettings } from "@/hooks/use-system-settings";

export function ThemeToggle() {
  const [theme, setTheme] = useState<"light" | "dark" | "system" | null>(null);
  const { getSettingValue } = useSystemSettings();

  useEffect(() => {
    // Initialize theme state
    const savedTheme = localStorage.getItem("theme") as "light" | "dark" | "system" | null;
    const defaultTheme = getSettingValue("app.darkMode", "system");
    setTheme(savedTheme || defaultTheme);
    
    // Apply theme on initial load
    applyTheme(savedTheme || defaultTheme);
  }, [getSettingValue]);

  const applyTheme = (newTheme: string) => {
    const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches 
      ? "dark" 
      : "light";
      
    // Update document class based on selected theme or system preference
    document.documentElement.classList.remove("light", "dark");
    if (newTheme === "system") {
      document.documentElement.classList.add(systemTheme);
    } else {
      document.documentElement.classList.add(newTheme);
    }
  };

  const setMode = (newTheme: "light" | "dark" | "system") => {
    setTheme(newTheme);
    
    // Update localStorage
    localStorage.setItem("theme", newTheme);
    
    // Apply the theme
    applyTheme(newTheme);
  };

  if (theme === null) return null; // Don't render until theme is initialized

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-full">
          {theme === "light" && <Sun className="h-[1.2rem] w-[1.2rem]" />}
          {theme === "dark" && <Moon className="h-[1.2rem] w-[1.2rem]" />}
          {theme === "system" && <Computer className="h-[1.2rem] w-[1.2rem]" />}
          <span className="sr-only">Zmeniť tému</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => setMode("light")}>
          <Sun className="mr-2 h-4 w-4" />
          <span>Svetlá</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setMode("dark")}>
          <Moon className="mr-2 h-4 w-4" />
          <span>Tmavá</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setMode("system")}>
          <Computer className="mr-2 h-4 w-4" />
          <span>Systémová</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}